// «Registrar movimiento» y traspasos abiertos: schema del formulario, traducción a los DTOs y reglas puras.
import { z } from 'zod';
import { ADJUSTMENT_REASONS } from '@fiado/type-kit/bin/retailCatalog/constants/AdjustmentReasons';
import { InventoryMovementReasonEnum } from '@fiado/type-kit/bin/retailCatalog/enums/InventoryMovementReasonEnum';
import { TransferStateEnum } from '@fiado/type-kit/bin/retailCatalog/enums/TransferStateEnum';
import { ApiError } from '@/api/errors';
import { isValidImei } from '@/lib/formValidators';
import type {
    BackofficeCreateTransferRequest,
    BackofficeRegisterAdjustmentRequest,
    InventoryStorePieces,
    RegisterEntryRequest,
    TransferResponse,
} from '@/api/services/retail-catalog/backoffice';
import type { TrackingMode } from '@/api/services/retail-catalog/catalog.contract';
import { movementReasonLabel, storeInfo, type StoreDirectory } from './inventoryDetail';

/** Tope de piezas por operación del silo (MOVEMENT_TOO_LARGE). */
export const MAX_MOVEMENT_PIECES = 30;
/** Tope de unidades por operación de un SKU por cantidad (VALIDATION_ERROR). */
export const MAX_MOVEMENT_QTY = 10_000;

export type MovementKind = 'ENTRY' | 'ADJUSTMENT' | 'TRANSFER';

export const MOVEMENT_KIND_LABEL: Record<MovementKind, string> = {
    ENTRY: 'Entrada',
    ADJUSTMENT: 'Merma o ajuste',
    TRANSFER: 'Traspaso a otra tienda',
};

/** Motivos que el admin puede registrar (ADJUSTMENT_REASONS de type-kit), en orden alfabético. */
export const ADJUSTMENT_REASON_OPTIONS: readonly {
    value: InventoryMovementReasonEnum;
    label: string;
}[] = ADJUSTMENT_REASONS.map((value) => ({ value, label: movementReasonLabel(value) })).sort(
    (a, b) => a.label.localeCompare(b.label),
);

export function tracksPieces(mode: TrackingMode | undefined): boolean {
    return mode === 'IMEI' || mode === 'SERIAL';
}

/** Identificadores capturados a mano: separa por renglón, coma o espacio y aparta los repetidos. */
export function parseIdentifierList(text: string): { identifiers: string[]; duplicates: string[] } {
    const identifiers: string[] = [];
    const duplicates: string[] = [];
    for (const token of text.split(/[\s,]+/)) {
        if (token === '') continue;
        if (identifiers.includes(token)) {
            if (!duplicates.includes(token)) duplicates.push(token);
            continue;
        }
        identifiers.push(token);
    }
    return { identifiers, duplicates };
}

const baseSchema = z.object({
    kind: z.enum(['ENTRY', 'ADJUSTMENT', 'TRANSFER']),
    storeId: z.string(),
    toStoreId: z.string(),
    reason: z.union([
        z.literal(''),
        z
            .nativeEnum(InventoryMovementReasonEnum)
            .refine((reason) => ADJUSTMENT_REASONS.includes(reason)),
    ]),
    qty: z.string(),
    identifiersText: z.string(),
    picked: z.array(z.string()),
    countSurplus: z.boolean(),
});

export type MovementForm = z.infer<typeof baseSchema>;

export const EMPTY_MOVEMENT_FORM: MovementForm = {
    kind: 'ENTRY',
    storeId: '',
    toStoreId: '',
    reason: '',
    qty: '1',
    identifiersText: '',
    picked: [],
    countSurplus: false,
};

const QTY_MESSAGE = 'Escribe una cantidad entera de 1 a 10 000.';
const TOO_MANY = 'Son hasta 30 piezas por movimiento. Hazlo en tandas.';

function parseQty(value: string): number | null {
    const trimmed = value.trim();
    if (!/^\d+$/.test(trimmed)) return null;
    const qty = Number(trimmed);
    return qty >= 1 && qty <= MAX_MOVEMENT_QTY ? qty : null;
}

/** Los IMEI capturados que no tienen 15 dígitos o fallan el dígito verificador. */
export function invalidImeisOf(identifiers: readonly string[]): string[] {
    return identifiers.filter((identifier) => !isValidImei(identifier));
}

function refineEntryPieces(form: MovementForm, imeiTracked: boolean, ctx: z.RefinementCtx) {
    const { identifiers, duplicates } = parseIdentifierList(form.identifiersText);
    const issue = (message: string) => {
        ctx.addIssue({ code: 'custom', path: ['identifiersText'], message });
    };
    const invalid = imeiTracked ? invalidImeisOf(identifiers) : [];
    if (identifiers.length === 0) issue('Escribe al menos un IMEI o número de serie.');
    else if (duplicates.length > 0) issue(`Quita los repetidos: ${duplicates.join(', ')}.`);
    else if (identifiers.length > MAX_MOVEMENT_PIECES) issue(TOO_MANY);
    else if (invalid.length > 0) {
        issue(`Revisa estos IMEI, cada uno lleva 15 dígitos válidos: ${invalid.join(', ')}.`);
    }
}

function refinePieces(form: MovementForm, imeiTracked: boolean, ctx: z.RefinementCtx) {
    if (form.kind === 'ENTRY') {
        refineEntryPieces(form, imeiTracked, ctx);
        return;
    }
    if (form.picked.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['picked'], message: 'Elige al menos una pieza.' });
    } else if (form.picked.length > MAX_MOVEMENT_PIECES) {
        ctx.addIssue({ code: 'custom', path: ['picked'], message: TOO_MANY });
    }
}

/** El schema depende del rastreo: por pieza se capturan identificadores (IMEI con Luhn), por cantidad la cantidad. */
export function movementFormSchema(withPieces: boolean, imeiTracked = false) {
    return baseSchema.superRefine((form, ctx) => {
        if (form.storeId === '') {
            ctx.addIssue({ code: 'custom', path: ['storeId'], message: 'Elige la tienda.' });
        }
        if (form.kind === 'ADJUSTMENT' && form.reason === '') {
            ctx.addIssue({ code: 'custom', path: ['reason'], message: 'Elige el motivo.' });
        }
        if (form.kind === 'TRANSFER') {
            if (form.toStoreId === '') {
                ctx.addIssue({
                    code: 'custom',
                    path: ['toStoreId'],
                    message: 'Elige la tienda destino.',
                });
            } else if (form.toStoreId === form.storeId) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['toStoreId'],
                    message: 'La tienda destino debe ser distinta a la de origen.',
                });
            }
        }
        if (withPieces) refinePieces(form, imeiTracked, ctx);
        else if (parseQty(form.qty) === null) {
            ctx.addIssue({ code: 'custom', path: ['qty'], message: QTY_MESSAGE });
        }
    });
}

export type MovementRequest =
    | { kind: 'ENTRY'; body: RegisterEntryRequest }
    | { kind: 'ADJUSTMENT'; body: BackofficeRegisterAdjustmentRequest }
    | { kind: 'TRANSFER'; body: BackofficeCreateTransferRequest };

/** Piezas del movimiento: las escritas en una entrada, las elegidas en merma o traspaso. */
export function movementIdentifiers(form: MovementForm): string[] {
    if (form.kind === 'ENTRY') return parseIdentifierList(form.identifiersText).identifiers;
    return [...form.picked].sort((a, b) => a.localeCompare(b));
}

/** Traduce un formulario ya validado al DTO del endpoint que le toca. */
export function toMovementRequest(
    form: MovementForm,
    sku: string,
    withPieces: boolean,
): MovementRequest {
    const identifiers = withPieces ? movementIdentifiers(form) : undefined;
    const qty = identifiers ? identifiers.length : (parseQty(form.qty) ?? 0);
    const pieces = identifiers ? { identifiers } : {};
    if (form.kind === 'TRANSFER') {
        return {
            kind: 'TRANSFER',
            body: { fromStoreId: form.storeId, toStoreId: form.toStoreId, sku, qty, ...pieces },
        };
    }
    if (form.kind === 'ADJUSTMENT') {
        return {
            kind: 'ADJUSTMENT',
            body: {
                storeId: form.storeId,
                sku,
                qty,
                reason: form.reason as InventoryMovementReasonEnum,
                ...pieces,
            },
        };
    }
    const reason = form.countSurplus ? { reason: 'COUNT_SURPLUS' as const } : {};
    return { kind: 'ENTRY', body: { storeId: form.storeId, sku, qty, ...pieces, ...reason } };
}

/** Piezas disponibles del SKU en una tienda, en orden alfabético. */
export function availablePieces(
    piecesByStore: readonly InventoryStorePieces[],
    storeId: string,
): string[] {
    const store = piecesByStore.find((s) => s.storeId === storeId);
    return (store?.pieces ?? [])
        .filter((p) => p.status === 'AVAILABLE')
        .map((p) => p.identifier)
        .sort((a, b) => a.localeCompare(b));
}

/** Tiendas del retailer para elegir origen o destino, en orden alfabético por código. */
export function storeOptionsOf(stores: StoreDirectory): { value: string; label: string }[] {
    return [...stores.entries()]
        .map(([storeId]) => ({ value: storeId, label: storeLabel(stores, storeId) }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

/** «T01 · Centro», para tablas y confirmaciones. */
export function storeLabel(stores: StoreDirectory, storeId: string): string {
    const info = storeInfo(stores, storeId);
    return `${info.code} · ${info.name}`;
}

/** Traspasos abiertos (del SKU si se pide); el más antiguo primero porque es la fila por recibir. */
export function openTransfersOf(
    items: readonly TransferResponse[],
    sku?: string,
): TransferResponse[] {
    return items
        .filter((t) => t.state === TransferStateEnum.OPEN && (sku === undefined || t.sku === sku))
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** La pieza que el silo rechazó (`data.identifier` del error), si la trae. */
export function failedIdentifierOf(error: unknown): string | null {
    if (!(error instanceof ApiError)) return null;
    const data = error.data as { identifier?: unknown } | null | undefined;
    return typeof data?.identifier === 'string' ? data.identifier : null;
}
